//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { numberFormat } from "humanize";
import React, { useEffect, useState } from "react";
import { Dimmer, Header, Icon, Loader, Message, Popup, Segment, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { UsageItemInfo as ApiUsageItemInfo } from "../../api/lib";
import { buildUsageItemsRequest, formatUsageItemKind, moneyAsNumber } from "./UsageItemList";

// How many matching items a single request will aggregate over. There is no
// count/total-only endpoint and ListAllUsageItemInfos returns no next-page
// token, so "aggregate across all pages" here means one request with a page
// size this large rather than looping page-by-page - the server applies the
// same filters and just LIMITs the AQL query (see usaged's getQueryLimit),
// so this is one query either way.
//
// 5000 comfortably covers what a support investigation actually filters
// down to (one deployment, one invoice, or a short date range for an org),
// while keeping the query itself cheap. If a filtered result set is larger
// than this, see the `capped` handling below - the total is never presented
// as exact when that happens.
export const ITEM_CAP = 5000;

interface IUsageCreditsSummaryArgs {
  organization_id?: string;
  project_id?: string;
  deploymentID?: string;
  from: Date;
  to: Date;
  selectedKind: string;
  invoiceID: string;
  hideEmpty: boolean;
}

interface IComponentTotal {
  kind: string;
  credits: number;
  count: number;
}

// Same fallback RowView in UsageItemList.tsx uses for its own Credits column:
// prefer the (deprecated) V1 `credits.total`, else Billing 2.0 `cost.credits`,
// else nothing. The two branches are mutually exclusive (an item never has
// both), so this never double-counts. Summing this - not inventing a separate
// notion of "credits" - is what makes this summary agree with the column it
// is summarizing. Exported for direct testing (see UsageCreditsSummary.test.ts).
export const creditsOf = (item: ApiUsageItemInfo): number => {
  if (item.credits) {
    return moneyAsNumber(item.credits.total);
  }
  if (item.cost && item.cost.credits) {
    return moneyAsNumber(item.cost.credits);
  }
  return 0;
};

// True once a fetch may have been truncated by ITEM_CAP - i.e. it cannot tell
// "exactly ITEM_CAP items exist" from "more exist and got cut off", so it
// treats the boundary itself as capped. That means this can only ever
// over-warn, never under-warn: the "must never look exact when it isn't"
// requirement fails safe. Exported for direct testing.
export const isCapped = (itemCount: number): boolean => itemCount >= ITEM_CAP;

// Shows the total credits (and a per-component breakdown) for every item
// matching the filters UsageItemFilterView above is currently set to - i.e.
// the same items UsageItemListView lists below, just summed across every
// page instead of only the one page that list currently displays.
//
// Scope of what's counted, same as the table: every ingestion status
// (including not-yet-billable/failed/skipped items - neither this nor the
// table filters by ingestion_status), prepaid-deployment items included, and
// either every kind, only Billing 2.0 kinds, or one specific kind depending
// on the Kind filter - exactly what "hideEmpty"/"selectedKind" above select.
// The on-screen caption below states the non-billable/failed/prepaid part
// explicitly - that is the exact fact a support engineer comparing this
// against an invoice must not have to infer from code or documentation.
const UsageCreditsSummary = ({ ...args }: IUsageCreditsSummaryArgs) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [totals, setTotals] = useState<IComponentTotal[]>([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [capped, setCapped] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    const req = buildUsageItemsRequest({
      organization_id: args.organization_id,
      project_id: args.project_id,
      deploymentID: args.deploymentID,
      from: args.from,
      to: args.to,
      selectedKind: args.selectedKind,
      invoiceID: args.invoiceID,
      hideEmpty: args.hideEmpty,
      page: 0,
      pageSize: ITEM_CAP,
    });
    apiClients.idashboardClient
      .ListAllUsageItemInfos(req)
      .then((result) => {
        if (cancelled) {
          return;
        }
        const items = result.items || [];
        const byKind = new Map<string, { credits: number; count: number }>();
        let total = 0;
        items.forEach((item) => {
          const kind = formatUsageItemKind((item.usage_item || {}).kind);
          const credits = creditsOf(item);
          total += credits;
          const existing = byKind.get(kind) || { credits: 0, count: 0 };
          byKind.set(kind, { credits: existing.credits + credits, count: existing.count + 1 });
        });
        setTotals(
          Array.from(byKind.entries())
            .map(([kind, v]) => ({ kind, credits: v.credits, count: v.count }))
            .sort((a, b) => b.credits - a.credits)
        );
        setTotalCredits(total);
        setItemCount(items.length);
        setCapped(isCapped(items.length));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setError((err && err.message) || String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the filter inputs themselves, NOT on a generic
    // "something reloaded" signal: UsageItemList's onNextPage/onPreviousPage
    // also call reloadUsageItems (to fetch the next 40-row page), but paging
    // never changes which items match the filters, so it must not re-issue
    // this (up to ITEM_CAP-row) aggregate query. Listing every filter field
    // here - and nothing page-related - is what guarantees that: page turns
    // don't touch organization_id/project_id/deploymentID/from/to/
    // selectedKind/invoiceID/hideEmpty, so none of these deps change, so this
    // effect does not re-run on a page click. from/to are the same Date
    // object references across a page turn too (only UsageItemList's `page`
    // state changes), so this list is safe to use as-is with React's
    // reference-equality dependency check.
  }, [args.organization_id, args.project_id, args.deploymentID, args.from, args.to, args.selectedKind, args.invoiceID, args.hideEmpty]);

  return (
    <Segment>
      {/* Overlays (dims) the previous filter's numbers while a new fetch is in
          flight, instead of the small easy-to-miss inline spinner this used
          to show next to the heading: this is idashboard's own established
          "content is loading" convention (see src/ui/_loading.tsx's Loading
          component and the same Dimmer+Loader pairing across
          src/dashboard/monitoring/*Modal.tsx), so it matches the rest of the
          app, and its disappearance is an unambiguous "this is now fully
          loaded" signal instead of a number that quietly changes under you
          once a big (up to ITEM_CAP-row) fetch finishes. */}
      <Dimmer inverted active={loading}>
        <Loader inverted active={loading}>
          Loading credit summary&hellip;
        </Loader>
      </Dimmer>
      <Header as="h5">Credit summary</Header>
      {error ? (
        <Message negative size="small">
          Failed to compute credit summary: {error}
        </Message>
      ) : (
        <>
          <p>
            <strong>{numberFormat(totalCredits)} credits</strong> total across {itemCount} usage item{itemCount === 1 ? "" : "s"} matching the filters above
            (all pages, not just the page listed below). Includes non-billable, failed-ingestion and prepaid-deployment items, so this can be larger than what
            was actually invoiced.
          </p>
          {capped && (
            <p style={{ color: "#9f3a38" }}>
              <Icon name="warning sign" color="orange" />
              Lower bound only &mdash; capped at {ITEM_CAP.toLocaleString()} items, so this is not the exact total.{" "}
              <Popup
                trigger={<Icon name="info circle" color="grey" style={{ cursor: "pointer" }} />}
                content="Narrow the date range or add a deployment/invoice filter above to get an exact number."
              />
            </p>
          )}
          {itemCount === 0 ? (
            <p>No usage items match the current filters.</p>
          ) : (
            <Table basic="very" compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Component</Table.HeaderCell>
                  <Table.HeaderCell>Items</Table.HeaderCell>
                  <Table.HeaderCell>Credits</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {totals.map((row) => (
                  <Table.Row key={row.kind}>
                    <Table.Cell>{row.kind}</Table.Cell>
                    <Table.Cell>{row.count}</Table.Cell>
                    <Table.Cell>{numberFormat(row.credits)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </Segment>
  );
};

export default UsageCreditsSummary;
